import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

/**
 * Reusable Markdown renderer component with rich Tailwind CSS styles for:
 * - GFM Tables (styled headers, alternating rows, clean borders)
 * - Headings (h1 - h4 badges with left accent borders)
 * - Lists (bullet & numbered)
 * - Bold highlights & code blocks
 */
const FormattedMarkdown = ({ content, className = "" }) => {
  if (!content) return null;

  // Normalize string content & strip wrapping markdown code fences if present (e.g. ```markdown ... ```)
  let textContent =
    typeof content === "string"
      ? content.replace(/\\n/g, "\n")
      : String(content);

  textContent = textContent
    .replace(/^```(?:markdown|md)?\s*\n?/i, "")
    .replace(/\n?```\s*$/i, "")
    .trim();

  return (
    <div className={`formatted-markdown text-slate-800 ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-3 rounded-lg border border-indigo-100 shadow-sm bg-white">
              <table
                className="min-w-full divide-y divide-indigo-100 text-xs sm:text-sm text-left border-collapse"
                {...props}
              />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead
              className="bg-gradient-to-r from-indigo-900 to-purple-900 text-white font-semibold uppercase tracking-wider"
              {...props}
            />
          ),
          th: ({ node, ...props }) => (
            <th
              className="px-3.5 py-2.5 font-bold tracking-wider text-white border-b border-indigo-800"
              {...props}
            />
          ),
          tbody: ({ node, ...props }) => (
            <tbody
              className="divide-y divide-gray-100 bg-white text-gray-700"
              {...props}
            />
          ),
          tr: ({ node, ...props }) => (
            <tr
              className="hover:bg-indigo-50/50 transition-colors odd:bg-white even:bg-slate-50/50"
              {...props}
            />
          ),
          td: ({ node, ...props }) => (
            <td
              className="px-3.5 py-2.5 align-top text-gray-700 leading-relaxed border-t border-gray-100"
              {...props}
            />
          ),
          h1: ({ node, ...props }) => (
            <h1
              className="text-lg font-bold text-indigo-950 mt-4 mb-2 border-b-2 border-indigo-200 pb-1"
              {...props}
            />
          ),
          h2: ({ node, ...props }) => (
            <h2
              className="text-base font-bold text-indigo-900 mt-4 mb-2 flex items-center gap-1.5"
              {...props}
            />
          ),
          h3: ({ node, ...props }) => (
            <h3
              className="text-sm font-bold text-indigo-900 bg-indigo-50/90 px-3 py-2 rounded-md border-l-4 border-indigo-600 mt-3 mb-2 shadow-xs"
              {...props}
            />
          ),
          h4: ({ node, ...props }) => (
            <h4
              className="text-xs sm:text-sm font-bold text-purple-900 mt-3 mb-1 border-b border-purple-100 pb-0.5"
              {...props}
            />
          ),
          p: ({ node, ...props }) => (
            <p
              className="text-gray-700 leading-relaxed mb-2 text-xs sm:text-sm"
              {...props}
            />
          ),
          ul: ({ node, ...props }) => (
            <ul
              className="list-disc list-outside ml-5 space-y-1 my-2 text-gray-700 text-xs sm:text-sm"
              {...props}
            />
          ),
          ol: ({ node, ...props }) => (
            <ol
              className="list-decimal list-outside ml-5 space-y-1 my-2 text-gray-700 text-xs sm:text-sm"
              {...props}
            />
          ),
          li: ({ node, ...props }) => (
            <li className="leading-relaxed" {...props} />
          ),
          strong: ({ node, ...props }) => (
            <strong
              className="font-semibold text-indigo-950 bg-indigo-50/80 px-1 py-0.5 rounded"
              {...props}
            />
          ),
          code: ({ node, inline, ...props }) => (
            <code
              className="bg-gray-100 text-purple-800 px-1.5 py-0.5 rounded text-xs font-mono border border-gray-200"
              {...props}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              className="border-l-4 border-purple-400 pl-3 py-1 italic bg-purple-50/40 text-gray-700 my-2 rounded-r-md text-xs sm:text-sm"
              {...props}
            />
          ),
          hr: ({ node, ...props }) => (
            <hr className="my-3 border-t border-indigo-200" {...props} />
          ),
        }}
      >
        {textContent}
      </ReactMarkdown>
    </div>
  );
};

export default FormattedMarkdown;
